"use client";

import { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { useI18n } from "@/lib/i18n";
import { useUser } from "@/lib/context/user-context";
import { 
  Loader2, 
  Send, 
  CheckCircle2, 
  AlertCircle,
  MessageSquare,
  Bug,
  Lightbulb,
  HelpCircle,
  Mail,
  Sparkles
} from "lucide-react";
import { cn } from "@/lib/utils";

interface HelpSupportDialogProps {
  isOpen: boolean;
  onClose: () => void;
}

type SendStatus = "idle" | "sending" | "success" | "error";
type FeedbackType = "question" | "bug" | "feature" | "other";

const feedbackTypes: { 
  value: FeedbackType; 
  labelEn: string; 
  labelZh: string; 
  icon: typeof HelpCircle; 
  color: string;
  bgColor: string;
  borderColor: string;
}[] = [
  { 
    value: "question", 
    labelEn: "Question", 
    labelZh: "问题咨询", 
    icon: HelpCircle, 
    color: "text-blue-600 dark:text-blue-400",
    bgColor: "bg-blue-500/10",
    borderColor: "border-blue-500/30"
  },
  { 
    value: "bug", 
    labelEn: "Bug", 
    labelZh: "Bug", 
    icon: Bug, 
    color: "text-rose-600 dark:text-rose-400",
    bgColor: "bg-rose-500/10",
    borderColor: "border-rose-500/30"
  },
  { 
    value: "feature", 
    labelEn: "Feature", 
    labelZh: "功能建议", 
    icon: Lightbulb, 
    color: "text-amber-600 dark:text-amber-400",
    bgColor: "bg-amber-500/10",
    borderColor: "border-amber-500/30"
  },
  { 
    value: "other", 
    labelEn: "Other", 
    labelZh: "其他", 
    icon: MessageSquare, 
    color: "text-violet-600 dark:text-violet-400",
    bgColor: "bg-violet-500/10",
    borderColor: "border-violet-500/30"
  },
];

export function HelpSupportDialog({ isOpen, onClose }: HelpSupportDialogProps) {
  const { lang } = useI18n() as any;
  const { user } = useUser();
  const isChinese = lang === 'chinese' || lang === 'zh';

  const [feedbackType, setFeedbackType] = useState<FeedbackType>("question");
  const [message, setMessage] = useState("");
  const [status, setStatus] = useState<SendStatus>("idle");
  const [errorMsg, setErrorMsg] = useState("");

  const userEmail = user?.email || "";

  const selectedType = feedbackTypes.find(t => t.value === feedbackType)!;

  const handleSubmit = async () => {
    if (!message.trim()) return;

    setStatus("sending");
    setErrorMsg("");

    const subject = `[${selectedType.labelEn}] ${isChinese ? "用户反馈" : "User Feedback"}`;

    try {
      const res = await fetch("/api/support/send", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          userEmail,
          subject,
          message: `Type: ${selectedType.labelEn}\n\n${message.trim()}`,
        }),
      });

      if (!res.ok) {
        const data = await res.json();
        throw new Error(data.error || "Failed to send message");
      }

      setStatus("success");
      setTimeout(() => {
        setMessage("");
        setFeedbackType("question");
        setStatus("idle");
        onClose();
      }, 2000);
    } catch (err: any) {
      setStatus("error");
      setErrorMsg(err?.message || (isChinese ? "发送失败，请稍后重试" : "Failed to send, please try again"));
    }
  };

  const handleClose = () => {
    if (status === "sending") return;
    setMessage("");
    setFeedbackType("question");
    setStatus("idle");
    setErrorMsg("");
    onClose();
  };

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && handleClose()}>
      <DialogContent className="sm:max-w-[440px] p-0 gap-0 overflow-hidden rounded-3xl border-0 shadow-2xl">
        {/* Header */}
        <div className="relative bg-gradient-to-br from-primary via-primary to-primary/80 px-6 pt-7 pb-6 text-white overflow-hidden">
          {/* Decorative elements */}
          <div className="absolute top-0 right-0 w-40 h-40 bg-white/10 rounded-full blur-3xl -translate-y-1/2 translate-x-1/2" />
          <div className="absolute bottom-0 left-0 w-32 h-32 bg-white/5 rounded-full blur-2xl translate-y-1/2 -translate-x-1/2" />
          <div className="absolute top-4 right-6 opacity-20">
            <Sparkles className="w-8 h-8" />
          </div>
          
          <DialogHeader className="relative space-y-2">
            <DialogTitle className={cn("text-2xl font-bold text-white", isChinese && "font-semibold")}>
              {isChinese ? "帮助与支持" : "Help & Support"}
            </DialogTitle>
            <DialogDescription className={cn("text-white/80 text-sm", isChinese && "font-normal")}>
              {isChinese
                ? "有任何问题或建议？我们很乐意听取您的反馈"
                : "Have questions or suggestions? We'd love to hear from you"}
            </DialogDescription>
          </DialogHeader>
        </div>

        {status === "success" ? (
          <div className="flex flex-col items-center justify-center py-14 px-6 gap-5 bg-gradient-to-b from-green-50/50 to-transparent dark:from-green-950/20">
            <div className="relative">
              <div className="absolute inset-0 bg-green-500/30 rounded-full blur-2xl scale-150 animate-pulse" />
              <div className="relative w-20 h-20 rounded-full bg-gradient-to-br from-green-400 to-emerald-600 flex items-center justify-center shadow-xl shadow-green-500/25">
                <CheckCircle2 className="w-10 h-10 text-white" strokeWidth={2.5} />
              </div>
            </div>
            <div className="text-center space-y-2">
              <p className={cn("text-xl font-bold text-foreground", isChinese && "font-semibold")}>
                {isChinese ? "发送成功！" : "Message Sent!"}
              </p>
              <p className={cn("text-sm text-muted-foreground max-w-[240px]", isChinese && "font-normal")}>
                {isChinese
                  ? "感谢您的反馈，我们会尽快回复您"
                  : "Thank you for your feedback. We'll get back to you soon."}
              </p>
            </div>
          </div>
        ) : (
          <div className="px-6 py-5 space-y-5">
            {/* Feedback Type Selector */}
            <div className="space-y-3">
              <label className={cn("text-sm font-medium text-foreground", isChinese && "font-normal")}>
                {isChinese ? "反馈类型" : "Type"}
              </label>
              <div className="grid grid-cols-4 gap-2">
                {feedbackTypes.map((type) => {
                  const Icon = type.icon;
                  const isSelected = feedbackType === type.value;
                  return (
                    <button
                      key={type.value}
                      type="button"
                      onClick={() => setFeedbackType(type.value)}
                      disabled={status === "sending"}
                      className={cn(
                        "group relative flex flex-col items-center gap-2 p-3 rounded-2xl border-2 transition-all duration-300",
                        "hover:scale-[1.02] active:scale-[0.98]",
                        isSelected
                          ? cn(type.bgColor, type.borderColor, "shadow-sm")
                          : "border-border/50 bg-muted/30 hover:bg-muted/50 hover:border-border"
                      )}
                    >
                      <div className={cn(
                        "w-9 h-9 rounded-xl flex items-center justify-center transition-all duration-300",
                        isSelected ? cn(type.bgColor) : "bg-muted group-hover:bg-muted/80"
                      )}>
                        <Icon className={cn(
                          "w-4.5 h-4.5 transition-colors duration-300", 
                          isSelected ? type.color : "text-muted-foreground group-hover:text-foreground/70"
                        )} />
                      </div>
                      <span className={cn(
                        "text-xs font-medium transition-colors duration-300",
                        isSelected ? "text-foreground" : "text-muted-foreground group-hover:text-foreground/80",
                        isChinese && "font-normal"
                      )}>
                        {isChinese ? type.labelZh : type.labelEn}
                      </span>
                    </button>
                  );
                })}
              </div>
            </div>

            {/* User Email */}
            {userEmail && (
              <div className="flex items-center gap-2.5 text-sm bg-muted/40 rounded-xl px-4 py-2.5 border border-border/30">
                <div className="w-7 h-7 rounded-lg bg-primary/10 flex items-center justify-center shrink-0">
                  <Mail className="w-3.5 h-3.5 text-primary" />
                </div>
                <span className="text-muted-foreground truncate text-sm">{userEmail}</span>
              </div>
            )}

            {/* Message */}
            <div className="space-y-2.5">
              <div className="flex items-center justify-between">
                <label className={cn("text-sm font-medium text-foreground", isChinese && "font-normal")}>
                  {isChinese ? "详细描述" : "Message"}
                  <span className="text-rose-500 ml-0.5">*</span>
                </label>
                <span className={cn(
                  "text-xs transition-colors",
                  message.length > 900 ? "text-amber-500" : "text-muted-foreground/60"
                )}>
                  {message.length}/1000
                </span>
              </div>
              <Textarea
                value={message}
                onChange={(e) => setMessage(e.target.value.slice(0, 1000))}
                placeholder={
                  isChinese
                    ? "请详细描述您遇到的问题或建议..."
                    : "Please describe your issue or suggestion..."
                }
                className={cn(
                  "min-h-[130px] resize-none rounded-xl border-border/50 bg-muted/30",
                  "focus:bg-background focus:border-primary/50 focus:ring-2 focus:ring-primary/10",
                  "placeholder:text-muted-foreground/50 transition-all duration-200"
                )}
                disabled={status === "sending"}
              />
            </div>

            {/* Error message */}
            {status === "error" && errorMsg && (
              <div className="flex items-start gap-3 text-sm bg-rose-500/10 border border-rose-500/20 rounded-xl p-3.5 animate-in fade-in slide-in-from-top-2">
                <div className="w-6 h-6 rounded-full bg-rose-500/20 flex items-center justify-center shrink-0">
                  <AlertCircle className="w-3.5 h-3.5 text-rose-600 dark:text-rose-400" />
                </div>
                <span className={cn("text-rose-700 dark:text-rose-300 pt-0.5", isChinese && "font-normal")}>
                  {errorMsg}
                </span>
              </div>
            )}

            {/* Actions */}
            <div className="flex items-center gap-3 pt-1">
              <Button
                variant="outline"
                onClick={handleClose}
                disabled={status === "sending"}
                className="flex-1 h-11 rounded-xl border-border/50 hover:bg-muted/50"
              >
                {isChinese ? "取消" : "Cancel"}
              </Button>
              <Button
                onClick={handleSubmit}
                disabled={!message.trim() || status === "sending"}
                className="flex-1 h-11 rounded-xl gap-2 shadow-lg shadow-primary/25 hover:shadow-primary/35 transition-shadow"
              >
                {status === "sending" ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin" />
                    <span>{isChinese ? "发送中" : "Sending"}</span>
                  </>
                ) : (
                  <>
                    <Send className="h-4 w-4" />
                    <span>{isChinese ? "发送反馈" : "Send"}</span>
                  </>
                )}
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
